import { useMemo, useState } from "react";
import { type PfpProject } from "@/pfp/generator";
import { useChainStore } from "@/stores/chain";
import { byId } from "@/lib/chains";
import { cn } from "@/lib/cn";
import { StatsPanel } from "@/components/StatsPanel";
import { TraitCard } from "@/components/pfp/TraitCard";
import { RuleSentence } from "@/components/pfp/RuleSentence";
import { HeroCard } from "@/components/pfp/HeroCard";
import { RollPreview } from "@/components/pfp/RollPreview";
import { SampleGallery } from "@/components/pfp/SampleGallery";
import { usePfpDeploy, usePfpProjectState } from "@/hooks/usePfpProject";

type Tab = "layers" | "rules" | "heroes" | "metadata";

/**
 * PFP studio page. Owns layout + tab orchestration; project state and
 * mutations live in usePfpProjectState; save/pin&deploy live in
 * usePfpDeploy. Sub-components (TraitCard, RuleSentence, HeroCard,
 * RollPreview, SampleGallery, StatsPanel) handle the heavy UI.
 */
export function Pfp() {
  const activeChainId = useChainStore((s) => s.activeChainId);
  const chain = byId(activeChainId);
  const {
    project,
    setProject,
    layers,
    traits,
    rules,
    heroes,
  } = usePfpProjectState();

  const [serverId, setServerId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("layers");
  const [count, setCount] = useState(20);
  const [rollSeed, setRollSeed] = useState(() => Date.now());
  const [galleryNonce, setGalleryNonce] = useState(0);

  const { saveProject, pinAndDeploy, busy, progress, status, setStatus } = usePfpDeploy({
    project,
    setProject,
    count,
    serverId,
    setServerId,
  });

  const layerOptions = project.layers.map((l) => ({ id: l.id, name: l.name, traits: l.traits }));

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "layers", label: "Layers", count: project.layers.length },
    { id: "rules", label: "Rules", count: project.rules.length },
    { id: "heroes", label: "Heroes", count: project.heroes.length },
    { id: "metadata", label: "Metadata" },
  ];

  const totalCombos = useMemo(() => {
    if (project.layers.length === 0) return 0;
    return project.layers.reduce((acc, l) => acc * Math.max(1, l.traits.length), 1);
  }, [project.layers]);

  const handleRoll = () => {
    setRollSeed(Date.now());
    setGalleryNonce((n) => n + 1);
  };

  const tryAddRule = (kind: Parameters<typeof rules.add>[0]) => {
    const result = rules.add(kind);
    if (!result.ok) setStatus(result.reason);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-1 flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="label">Collection name</span>
            <input
              className="input h-9 w-64"
              value={project.name}
              onChange={(e) => setProject((p) => ({ ...p, name: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="label">Canvas (px)</span>
            <input
              type="number"
              className="input h-9 w-20"
              value={project.canvasSize.width}
              onChange={(e) => {
                const n = Math.max(64, Math.min(2048, Number(e.target.value) || 512));
                setProject((p) => ({ ...p, canvasSize: { width: n, height: n } }));
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="label">Tokens to mint</span>
            <input
              type="number"
              className="input h-9 w-20"
              min={1}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(10000, Number(e.target.value) || 1)))}
            />
          </label>
        </div>
        <div className="flex items-end gap-2">
          <button
            className="btn-secondary h-9 px-3 text-xs"
            onClick={() => void saveProject()}
            disabled={busy}
          >
            Save
          </button>
          <button
            className="btn-primary h-9 px-3 text-xs"
            disabled={busy || project.layers.length === 0}
            onClick={() => void pinAndDeploy()}
          >
            {busy ? "Working…" : `Pin & deploy ${count}`}
          </button>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <main>
          <div className="mb-4 flex items-center gap-1 border-b border-border">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition",
                  tab === t.id
                    ? "text-foreground after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
                {typeof t.count === "number" && t.count > 0 && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                    {t.count}
                  </span>
                )}
              </button>
            ))}
            <span className="ml-auto pr-2 text-xs text-muted-foreground">
              {project.layers.length} layers · {totalCombos.toLocaleString()} combos
            </span>
          </div>

          {tab === "layers" && (
            <div className="space-y-4">
              {project.layers.length === 0 && (
                <div className="card text-center text-sm text-muted-foreground">
                  Start by adding a layer (background, body, eyes…). Drop in PNG/JPEG/WebP files for each trait.
                </div>
              )}
              {project.layers.map((l, idx) => (
                <section key={l.id} className="card">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">#{idx + 1}</span>
                    <input
                      className="input h-8 w-52 font-medium"
                      value={l.name}
                      onChange={(e) => layers.rename(l.id, e.target.value)}
                    />
                    <button
                      className="btn-ghost h-8 px-2 text-xs"
                      onClick={() => layers.move(l.id, -1)}
                      disabled={idx === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      className="btn-ghost h-8 px-2 text-xs"
                      onClick={() => layers.move(l.id, 1)}
                      disabled={idx === project.layers.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <label className="btn-secondary h-8 cursor-pointer px-3 text-xs">
                      + Add traits
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        multiple
                        className="hidden"
                        onChange={(e) => traits.add(l.id, e.target.files)}
                      />
                    </label>
                    <button
                      className="btn-ghost ml-auto h-8 px-2 text-xs text-muted-foreground"
                      onClick={() => layers.remove(l.id)}
                    >
                      Remove layer
                    </button>
                  </div>
                  {l.traits.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No traits yet. Add some images.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                      {(() => {
                        const totalWeight = l.traits.reduce(
                          (s, x) => s + Math.max(0, x.weight),
                          0,
                        );
                        return l.traits.map((t) => (
                          <TraitCard
                            key={t.id}
                            trait={t}
                            totalWeight={totalWeight}
                            onChange={(patch) => traits.update(l.id, t.id, patch)}
                            onRemove={() => traits.remove(l.id, t.id)}
                          />
                        ));
                      })()}
                    </div>
                  )}
                </section>
              ))}
              <button className="btn-secondary h-9 px-3 text-xs" onClick={layers.add}>
                + Add layer
              </button>
            </div>
          )}

          {tab === "rules" && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  className="btn-ghost h-8 px-2 text-xs"
                  onClick={() => tryAddRule("incompatible")}
                  title="Two traits can never appear together"
                >
                  + Block combo
                </button>
                <button
                  className="btn-ghost h-8 px-2 text-xs"
                  onClick={() => tryAddRule("requires")}
                  title="When trait A appears, force trait B too"
                >
                  + Force pairing
                </button>
                <button
                  className="btn-ghost h-8 px-2 text-xs"
                  onClick={() => tryAddRule("weightShift")}
                  title="Make a trait more or less likely when another appears"
                >
                  + Tilt rarity
                </button>
              </div>
              {project.rules.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No rules yet — every combination is allowed and rolled by weight. Add rules above to block, force, or
                  bias.
                </p>
              )}
              {project.rules.map((rule, i) => (
                <RuleSentence
                  key={i}
                  rule={rule}
                  layers={layerOptions}
                  onChange={(patch) => rules.update(i, patch)}
                  onRemove={() => rules.remove(i)}
                />
              ))}
            </div>
          )}

          {tab === "heroes" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button className="btn-secondary h-8 px-3 text-xs" onClick={heroes.add}>
                  + Pin a token
                </button>
                <span className="text-xs text-muted-foreground">
                  Heroes lock specific tokens to specific trait combos — useful for founders, 1/1s, or reserved slots.
                </span>
              </div>
              {project.heroes.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No heroes pinned. Procedural generation will fill every token.
                </p>
              )}
              <div className="grid gap-3 md:grid-cols-2">
                {project.heroes.map((h) => (
                  <HeroCard
                    key={h.tokenId}
                    hero={h}
                    project={project}
                    onUpdate={(layerId, traitId) => heroes.update(h.tokenId, layerId, traitId)}
                    onRemove={() => heroes.remove(h.tokenId)}
                    onTokenIdChange={(newId) => heroes.setTokenId(h.tokenId, newId)}
                  />
                ))}
              </div>
            </div>
          )}

          {tab === "metadata" && (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="label">Name pattern</span>
                  <input
                    className="input"
                    value={project.metadata.namePattern}
                    onChange={(e) =>
                      setProject((p) => ({
                        ...p,
                        metadata: { ...p.metadata, namePattern: e.target.value },
                      }))
                    }
                    placeholder="{collection} #{id}"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="label">External URL (optional)</span>
                  <input
                    className="input"
                    value={project.metadata.externalUrl ?? ""}
                    onChange={(e) =>
                      setProject((p) => {
                        const v = e.target.value;
                        const md: PfpProject["metadata"] = { ...p.metadata };
                        if (v) md.externalUrl = v;
                        else delete md.externalUrl;
                        return { ...p, metadata: md };
                      })
                    }
                    placeholder="https://…"
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1">
                <span className="label">Description template</span>
                <textarea
                  className="input min-h-[80px]"
                  value={project.metadata.descriptionTemplate ?? ""}
                  onChange={(e) =>
                    setProject((p) => ({
                      ...p,
                      metadata: { ...p.metadata, descriptionTemplate: e.target.value },
                    }))
                  }
                  placeholder="Use {id} and {trait_<layerId>} for substitution."
                />
              </label>
            </div>
          )}

          {progress && (
            <div className="mt-4 text-xs text-muted-foreground">
              {progress.done} / {progress.total}
            </div>
          )}
          {status && <div className="mt-2 text-sm text-muted-foreground">{status}</div>}

          {chain && !chain.contracts.erc721Factory && (
            <p className="mt-6 text-xs text-amber-700">
              Note: no factory deployed on the active chain — generation works but Pin & Deploy can't finish.
            </p>
          )}
        </main>

        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <RollPreview project={project} seed={rollSeed} onRoll={handleRoll} />
          <SampleGallery project={project} nonce={galleryNonce} />
          <StatsPanel project={project} mintCount={count} />
        </aside>
      </div>
    </div>
  );
}
